import { useEffect, useState } from 'react';

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { GET_USER_PATH, ZOOM_LOGIN_REDIRECT_PATH } from '../config/apiPaths';
import { type GetUser } from '../types/apiTypes';
import { getEnvironment } from '../utils/environment';
import { getUrls } from '../utils/url';

export interface StateQueryParam {
    stage: string;
    target: string;
    user_uuid: string;
    school_name: string;
    showOrgType?: 'true';
    showExperimentalSettings?: 'true';
}

export const useUserUuid = (targetPath: string | null) => {
    const { account, environment } = getEnvironment();
    const { api } = getUrls();
    const [userUuid, setUserUuid] = useState<string | null>(null);

    useEffect(() => {
        const redirectToLogin = (localUserUuid: string) => {
            const search = new URLSearchParams(window.location.search);
            const showOrgType = search.get('showOrgType') === 'true' ? '&showOrgType=true' : '';
            const showExperimentalSettings = search.get('showExperimentalSettings') === 'true' ? '&showExperimentalSettings=true' : '';

            const state: StateQueryParam = {
                stage: environment,
                target: targetPath!,
                user_uuid: localUserUuid,
                school_name: account
            };

            if (showOrgType) state.showOrgType = 'true';
            if (showExperimentalSettings) state.showExperimentalSettings = 'true';

            window.location.href = encodeURI(`${api}${ZOOM_LOGIN_REDIRECT_PATH}?state=${JSON.stringify(state)}`);
        };

        const getUserUuid = () => {
            const localUserUuid = window.localStorage.getItem('user_uuid') || uuidv4();
            window.localStorage.setItem('user_uuid', localUserUuid);
            window.localStorage.setItem('user_uuid_time', String(new Date().getTime()));
            setUserUuid(localUserUuid);
            return localUserUuid;
        };

        const authenticateZoomUser = async (localUserUuid: string) => {
            if (!targetPath) {
                return;
            }

            await axios.get<GetUser>(`${api}${GET_USER_PATH}`, { params: { user_uuid: localUserUuid } })
                .then((successResponse) => {
                    if (!successResponse.data || successResponse.data?.zoom_login_id === '-1') {
                        redirectToLogin(localUserUuid);
                    }
                },
                (error) => {
                    if (error.response.status < 500) {
                        redirectToLogin(localUserUuid);
                    } else {
                        console.error(error);
                    }
                });
        };

        const localUserUuid = getUserUuid();
        authenticateZoomUser(localUserUuid);
    }, [account, api, environment, targetPath]);

    return userUuid;
};
